import {
  ArrowLeft,
  Building2,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Hash,
  LayoutDashboard,
  Library,
  Music,
  Plus,
  RefreshCcw,
  Search,
  SlidersHorizontal,
  Star,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../../../../auth/useAuth";
import ChurchLogo from "../../../../assets/logos/Church-Logo.png";
import HymnsIcon from "../../../../assets/logos/Hymns Icon.png";
import PanelLoadingState from "../../../../core/components/PanelLoadingState";
import useResponsive from "../../../../core/hooks/useResponsive";
import { churchMobileNavStyles } from "../../styles/churchMobileNavStyles";
import HymnsSettings from "./HymnsSettings";
import {
  addHymnToService,
  buildDefaultWeeklyHymnSlots,
  getCurrentHymnWeekStartDate,
  listWeeklyHymnPlans,
  loadChurchHymns,
  loadFavoriteHymnIds,
  loadHymnUsageStats,
  loadWeeklyHymnPlan,
  saveFavoriteHymnIds,
  saveWeeklyHymnPlan,
  searchChurchHymns,
  updateChurchHymnTitle,
} from "../../services/hymnService";
import { fetchOrganizationAccess } from "../../services/churchOrganizationAccessService";

const HYMNS_SECTIONS = [
  { id: "overview", label: "Overview", shortLabel: "Overview", icon: LayoutDashboard },
  { id: "selector", label: "Hymn Selector", shortLabel: "Select", icon: CalendarDays },
  { id: "library", label: "Hymn Library", shortLabel: "Library", icon: Library },
  { id: "settings", label: "Settings", shortLabel: "Settings", icon: SlidersHorizontal },
];
const HYMNS_SECTION_IDS = HYMNS_SECTIONS.map((section) => section.id);

function getInitialHymnsSection(sectionId) {
  return HYMNS_SECTION_IDS.includes(sectionId) ? sectionId : "overview";
}

function getWorkspaceName(user, account = null) {
  return (
    account?.name ||
    user?.churchOrgSession?.account?.name ||
    user?.account?.name ||
    user?.organization?.name ||
    "Church Workspace"
  );
}

function getOrganizationLogoUrl(user, account = null) {
  return (
    account?.logo_url ||
    account?.logoUrl ||
    user?.churchOrgSession?.account?.logo_url ||
    user?.churchOrgSession?.account?.logoUrl ||
    user?.account?.logo_url ||
    user?.account?.logoUrl ||
    user?.organization?.logo_url ||
    user?.organization?.logoUrl ||
    ChurchLogo
  );
}

function getHymnLabel(hymn) {
  return hymn?.title || (hymn?.songNumber ? `Song ${hymn.songNumber}` : "Untitled hymn");
}

function getHymnNumberValue(hymn) {
  const match = String(hymn?.songNumber || "").match(/\d+/);
  return match ? Number(match[0]) : 0;
}

function getHymnHundredGroup(hymn) {
  const hymnNumber = getHymnNumberValue(hymn);

  if (!hymnNumber) {
    return {
      id: "unnumbered",
      label: "Unnumbered",
      sortOrder: Number.MAX_SAFE_INTEGER,
    };
  }

  const start = Math.floor((hymnNumber - 1) / 200) * 200 + 1;
  const end = start + 199;

  return {
    id: `${start}-${end}`,
    label: `${start}-${end}`,
    sortOrder: start,
  };
}

function getNextOpenWeekSlotIndex(slots = [], afterSlotIndex = 0) {
  const laterOpenSlot = slots.find((slot) => !slot.hymnId && slot.slotIndex > afterSlotIndex);
  if (laterOpenSlot) return laterOpenSlot.slotIndex;

  const firstOpenSlot = slots.find((slot) => !slot.hymnId);
  return firstOpenSlot?.slotIndex || afterSlotIndex || 1;
}

export default function HymnsPage({ useStandaloneRoutes = false, initialSection = "" }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isPhone } = useResponsive();
  const [activeSection, setActiveSection] = useState(() => getInitialHymnsSection(initialSection));
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [editingId, setEditingId] = useState("");
  const [draftTitle, setDraftTitle] = useState("");
  const [query, setQuery] = useState("");
  const [hymns, setHymns] = useState([]);
  const [expandedLibraryGroups, setExpandedLibraryGroups] = useState({});
  const [favoriteHymnIds, setFavoriteHymnIds] = useState([]);
  const [usageCounts, setUsageCounts] = useState({});
  const [selectedHymnId, setSelectedHymnId] = useState("");
  const [selectorWeekStart, setSelectorWeekStart] = useState(() => getCurrentHymnWeekStartDate());
  const [weekHymnSlots, setWeekHymnSlots] = useState(() => buildDefaultWeeklyHymnSlots());
  const [savedWeekHymnSlots, setSavedWeekHymnSlots] = useState(() => buildDefaultWeeklyHymnSlots());
  const [activeWeekSlotIndex, setActiveWeekSlotIndex] = useState(1);
  const [weekSlotQueries, setWeekSlotQueries] = useState({});
  const [weekSlotFavoriteLists, setWeekSlotFavoriteLists] = useState({});
  const [weeklyPlanSaveStatus, setWeeklyPlanSaveStatus] = useState("Saved");
  const [savedWeekPlans, setSavedWeekPlans] = useState([]);
  const [selectorLoading, setSelectorLoading] = useState(false);
  const [previewSlideIndex, setPreviewSlideIndex] = useState(0);
  const [previewSlideLoading, setPreviewSlideLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [organizationAccount, setOrganizationAccount] = useState(null);
  const [favoritePlanHymn, setFavoritePlanHymn] = useState(null);
  const [favoritePlanWeekStart, setFavoritePlanWeekStart] = useState(() => getCurrentHymnWeekStartDate());
  const [favoritePlanSlotIndex, setFavoritePlanSlotIndex] = useState(1);

  async function refreshHymns() {
    setLoading(true);
    setError("");

    try {
      const [hymnResult, usageResult] = await Promise.all([
        loadChurchHymns(user),
        loadHymnUsageStats(),
      ]);
      const nextHymns = hymnResult.hymns || [];
      setHymns(nextHymns);
      setUsageCounts(usageResult || {});
      await Promise.all([
        refreshWeeklyPlan(selectorWeekStart, nextHymns),
        refreshSavedWeekPlans(),
      ]);
    } catch (loadError) {
      console.error("Hymns load error:", loadError);
      setError(loadError?.message || "Could not load hymns.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refreshHymns();
  }, [user?.id]);

  useEffect(() => {
    let isMounted = true;

    async function refreshFavoriteHymns() {
      const nextFavoriteHymnIds = await loadFavoriteHymnIds(user).catch(() => []);
      if (isMounted) {
        setFavoriteHymnIds(nextFavoriteHymnIds);
      }
    }

    refreshFavoriteHymns();

    return () => {
      isMounted = false;
    };
  }, [
    user?.id,
    user?.churchOrgSession?.orgUser?.id,
    user?.churchOrgSession?.account?.id,
  ]);

  useEffect(() => {
    let isMounted = true;

    async function loadOrganizationAccount() {
      if (!user?.id) {
        setOrganizationAccount(null);
        return;
      }

      const access = await fetchOrganizationAccess(user.id).catch(() => null);
      if (isMounted) {
        setOrganizationAccount(access?.account || null);
      }
    }

    loadOrganizationAccount();

    return () => {
      isMounted = false;
    };
  }, [user?.id]);

  useEffect(() => {
    if (initialSection) {
      setActiveSection(getInitialHymnsSection(initialSection));
    }
  }, [initialSection]);

  useEffect(() => {
    if (!loading) {
      refreshWeeklyPlan(selectorWeekStart, hymns);
    }
  }, [selectorWeekStart]);

  useEffect(() => {
    function handleSettingsSlotLabelUpdate(event) {
      applyWeeklySlotLabels(event.detail?.labels || []);
    }

    window.addEventListener("oikos:church:hymn-slot-labels-updated", handleSettingsSlotLabelUpdate);
    return () => {
      window.removeEventListener("oikos:church:hymn-slot-labels-updated", handleSettingsSlotLabelUpdate);
    };
  }, []);

  const hasLibrarySearch = query.trim().length > 0;
  const filteredHymns = useMemo(
    () => searchChurchHymns(hymns, query),
    [hymns, query]
  );
  const hymnsById = useMemo(
    () => new Map(hymns.map((hymn) => [hymn.id, hymn])),
    [hymns]
  );
  const favoriteHymnIdSet = useMemo(
    () => new Set(favoriteHymnIds),
    [favoriteHymnIds]
  );
  const favoriteHymns = useMemo(
    () => favoriteHymnIds
      .map((hymnId) => hymnsById.get(hymnId))
      .filter(Boolean),
    [favoriteHymnIds, hymnsById]
  );
  const libraryHymnGroups = useMemo(() => {
    const groups = new Map();

    hymns.forEach((hymn) => {
      const group = getHymnHundredGroup(hymn);
      const currentGroup = groups.get(group.id) || {
        ...group,
        hymns: [],
      };
      currentGroup.hymns.push(hymn);
      groups.set(group.id, currentGroup);
    });

    return Array.from(groups.values())
      .map((group) => ({
        ...group,
        hymns: group.hymns.slice().sort((left, right) => {
          const leftNumber = getHymnNumberValue(left);
          const rightNumber = getHymnNumberValue(right);
          if (leftNumber !== rightNumber) return leftNumber - rightNumber;
          return String(left.title || "").localeCompare(String(right.title || ""));
        }),
      }))
      .sort((left, right) => left.sortOrder - right.sortOrder);
  }, [hymns]);
  const selectedWeekHymns = useMemo(
    () => weekHymnSlots
      .map((slot) => {
        const hymn = hymnsById.get(slot.hymnId);
        return hymn
          ? {
              ...hymn,
              slotIndex: slot.slotIndex,
              slotLabel: slot.label,
            }
          : null;
      })
      .filter(Boolean),
    [hymnsById, weekHymnSlots]
  );
  const selectedWeekSlots = useMemo(
    () => weekHymnSlots.map((slot) => ({
      ...slot,
      hymn: hymnsById.get(slot.hymnId) || null,
    })),
    [hymnsById, weekHymnSlots]
  );
  const selectorHasChanges = JSON.stringify(
    weekHymnSlots.map((slot) => ({ label: slot.label, hymnId: slot.hymnId }))
  ) !== JSON.stringify(
    savedWeekHymnSlots.map((slot) => ({ label: slot.label, hymnId: slot.hymnId }))
  );

  useEffect(() => {
    if (loading || selectorLoading || !selectorHasChanges) {
      if (!selectorHasChanges) {
        setWeeklyPlanSaveStatus("Saved");
      }
      return undefined;
    }

    setWeeklyPlanSaveStatus("Saving...");
    const saveTimer = window.setTimeout(() => {
      handleSaveWeeklyPlan({ silent: true });
    }, 650);

    return () => window.clearTimeout(saveTimer);
  }, [loading, selectorLoading, selectorHasChanges, selectedWeekSlots, selectorWeekStart]);

  const topHymns = useMemo(() => {
    const ranked = hymns
      .map((hymn) => ({ ...hymn, usageCount: usageCounts[hymn.id] || 0 }))
      .sort((left, right) => {
        if (right.usageCount !== left.usageCount) {
          return right.usageCount - left.usageCount;
        }
        return String(left.songNumber || left.title).localeCompare(String(right.songNumber || right.title));
      });

    return ranked.slice(0, 3);
  }, [hymns, usageCounts]);

  const selectedHymn = useMemo(
    () => hymnsById.get(selectedHymnId) || null,
    [hymnsById, selectedHymnId]
  );
  const selectedSlides = Array.isArray(selectedHymn?.slides) ? selectedHymn.slides : [];
  const selectedPreviewSlide = selectedSlides[previewSlideIndex] || selectedSlides[0] || null;

  useEffect(() => {
    setPreviewSlideIndex(0);
    setPreviewSlideLoading(Boolean(selectedHymn?.slides?.[0]?.imageUrl));
  }, [selectedHymn?.id]);

  useEffect(() => {
    if (!selectedSlides.length) {
      setPreviewSlideIndex(0);
      setPreviewSlideLoading(false);
      return;
    }

    if (previewSlideIndex >= selectedSlides.length) {
      setPreviewSlideIndex(selectedSlides.length - 1);
    }
  }, [previewSlideIndex, selectedSlides.length]);

  async function handleAddToService(hymn) {
    setBusyId(hymn.id);
    setMessage("");
    setError("");

    try {
      const result = await addHymnToService(user?.id, hymn);
      setUsageCounts((current) => ({
        ...current,
        [hymn.id]: (current[hymn.id] || 0) + 1,
      }));
      setMessage(`${getHymnLabel(hymn)} added to ${result.serviceId}.`);
    } catch (actionError) {
      console.error("Add hymn to service error:", actionError);
      setError(actionError?.message || "Could not add the hymn to service.");
    } finally {
      setBusyId("");
    }
  }

  async function toggleFavoriteHymn(hymn) {
    if (!hymn?.id) return;

    const wasFavorite = favoriteHymnIdSet.has(hymn.id);
    const nextFavoriteIds = wasFavorite
      ? favoriteHymnIds.filter((hymnId) => hymnId !== hymn.id)
      : [hymn.id, ...favoriteHymnIds.filter((hymnId) => hymnId !== hymn.id)];
    const previousFavoriteIds = favoriteHymnIds;

    setFavoriteHymnIds(nextFavoriteIds);
    setError("");

    try {
      const savedFavoriteIds = await saveFavoriteHymnIds(user, nextFavoriteIds);
      setFavoriteHymnIds(savedFavoriteIds);
    } catch (favoriteError) {
      console.error("Favorite hymn save error:", favoriteError);
      setFavoriteHymnIds(previousFavoriteIds);
      setError(favoriteError?.message || "Could not save that favorite hymn.");
    }
  }

  async function refreshWeeklyPlan(weekStart = selectorWeekStart, sourceHymns = hymns) {
    setSelectorLoading(true);
    setError("");

    try {
      const result = await loadWeeklyHymnPlan(user, weekStart, sourceHymns);
      const slots = Array.isArray(result.slots) && result.slots.length
        ? result.slots
        : buildDefaultWeeklyHymnSlots().map((slot, index) => ({
            ...slot,
            hymnId: result.hymns?.[index]?.id || "",
      }));
      setWeekHymnSlots(slots);
      setSavedWeekHymnSlots(slots);
      setActiveWeekSlotIndex(getNextOpenWeekSlotIndex(slots, 0));
      setWeeklyPlanSaveStatus("Saved");
    } catch (loadError) {
      console.error("Weekly hymn plan load error:", loadError);
      setError(loadError?.message || "Could not load this week's hymn selector.");
    } finally {
      setSelectorLoading(false);
    }
  }

  async function refreshSavedWeekPlans() {
    try {
      const plans = await listWeeklyHymnPlans(user, 26);
      setSavedWeekPlans(plans);
    } catch (loadError) {
      console.error("Weekly hymn plan list error:", loadError);
    }
  }

  function addHymnToWeekSlot(hymn, slotIndex) {
    if (!hymn?.id) return;
    setWeekHymnSlots((current) => {
      const targetSlotIndex = Number(slotIndex) || 1;
      const nextSlots = current.map((slot) => {
        if (slot.hymnId === hymn.id && slot.slotIndex !== targetSlotIndex) {
          return { ...slot, hymnId: "" };
        }

        return slot.slotIndex === targetSlotIndex ? { ...slot, hymnId: hymn.id } : slot;
      });

      setError("");
      setActiveWeekSlotIndex(getNextOpenWeekSlotIndex(nextSlots, targetSlotIndex));
      setWeekSlotQueries((currentQueries) => ({
        ...currentQueries,
        [targetSlotIndex]: "",
      }));
      setWeekSlotFavoriteLists((currentLists) => ({
        ...currentLists,
        [targetSlotIndex]: false,
      }));
      return nextSlots;
    });
  }

  function updateWeekSlotSearch(slotIndex, value) {
    setActiveWeekSlotIndex(slotIndex);
    setWeekSlotQueries((currentQueries) => ({
      ...currentQueries,
      [slotIndex]: value,
    }));
    if (String(value || "").trim()) {
      setWeekSlotFavoriteLists((currentLists) => ({
        ...currentLists,
        [slotIndex]: false,
      }));
    }
  }

  function toggleWeekSlotFavorites(slotIndex) {
    setActiveWeekSlotIndex(slotIndex);
    setWeekSlotQueries((currentQueries) => ({
      ...currentQueries,
      [slotIndex]: "",
    }));
    setWeekSlotFavoriteLists((currentLists) => ({
      ...currentLists,
      [slotIndex]: !currentLists[slotIndex],
    }));
  }

  function openFavoritePlanModal(hymn) {
    if (!hymn?.id) return;
    setFavoritePlanHymn(hymn);
    setFavoritePlanWeekStart(selectorWeekStart || getCurrentHymnWeekStartDate());
    setFavoritePlanSlotIndex(activeWeekSlotIndex || 1);
    setMessage("");
    setError("");
  }

  function closeFavoritePlanModal() {
    setFavoritePlanHymn(null);
  }

  async function handleAddFavoriteToServiceList() {
    if (!favoritePlanHymn?.id) return;

    const targetWeekStart = favoritePlanWeekStart || getCurrentHymnWeekStartDate();
    const targetSlotIndex = Number(favoritePlanSlotIndex) || 1;
    setBusyId("favorite-plan");
    setMessage("");
    setError("");

    try {
      const existingPlan = await loadWeeklyHymnPlan(user, targetWeekStart, hymns);
      const sourceSlots = Array.isArray(existingPlan.slots) && existingPlan.slots.length
        ? existingPlan.slots
        : buildDefaultWeeklyHymnSlots();
      const nextSlots = sourceSlots.map((slot) => {
        if (slot.hymnId === favoritePlanHymn.id && slot.slotIndex !== targetSlotIndex) {
          return { ...slot, hymnId: "", hymn: null };
        }

        return slot.slotIndex === targetSlotIndex
          ? { ...slot, hymnId: favoritePlanHymn.id, hymn: favoritePlanHymn }
          : slot;
      });
      const savedPlan = await saveWeeklyHymnPlan(user, targetWeekStart, nextSlots);
      const savedSlots = Array.isArray(savedPlan.slots) && savedPlan.slots.length ? savedPlan.slots : nextSlots;

      if (targetWeekStart === selectorWeekStart) {
        setWeekHymnSlots(savedSlots);
        setSavedWeekHymnSlots(savedSlots);
        setActiveWeekSlotIndex(getNextOpenWeekSlotIndex(savedSlots, targetSlotIndex));
      }

      await refreshSavedWeekPlans();
      setWeeklyPlanSaveStatus("Saved");
      setMessage(`Added ${getHymnLabel(favoritePlanHymn)} to ${targetWeekStart}, block ${targetSlotIndex}.`);
      closeFavoritePlanModal();
    } catch (planError) {
      console.error("Favorite hymn weekly plan save error:", planError);
      setError(planError?.message || "Could not add that favorite hymn to the service list.");
    } finally {
      setBusyId("");
    }
  }

  function applyWeeklySlotLabels(labels = []) {
    const applyLabels = (slots = []) =>
      labels.map((label, index) => {
        const existingSlot = slots.find((slot) => slot.slotIndex === index + 1) || slots[index] || {};

        return {
          ...existingSlot,
          slotIndex: index + 1,
          label: String(label || existingSlot.label || `Hymn ${index + 1}`).trim() || `Hymn ${index + 1}`,
          hymnId: existingSlot.hymnId || "",
        };
      });

    setWeekHymnSlots((current) => applyLabels(current));
  }

  async function handleSaveWeeklyPlan({ silent = false } = {}) {
    setBusyId("weekly-plan");
    setWeeklyPlanSaveStatus("Saving...");
    if (!silent) setMessage("");
    setError("");

    try {
      const result = await saveWeeklyHymnPlan(user, selectorWeekStart, selectedWeekSlots);
      const slots = Array.isArray(result.slots) && result.slots.length ? result.slots : weekHymnSlots;
      const ids = slots.map((slot) => slot.hymnId).filter(Boolean);
      setWeekHymnSlots(slots);
      setSavedWeekHymnSlots(slots);
      setActiveWeekSlotIndex(getNextOpenWeekSlotIndex(slots, activeWeekSlotIndex));
      await refreshSavedWeekPlans();
      setWeeklyPlanSaveStatus("Saved");
      if (!silent) {
        setMessage(`Saved ${ids.length} hymn${ids.length === 1 ? "" : "s"} for the week of ${selectorWeekStart}.`);
      }
    } catch (saveError) {
      console.error("Weekly hymn plan save error:", saveError);
      setWeeklyPlanSaveStatus("Save failed");
      setError(saveError?.message || "Could not save this week's hymn selector.");
    } finally {
      setBusyId("");
    }
  }

  function startEditingTitle(hymn) {
    setEditingId(hymn.id);
    setDraftTitle(hymn.title || "");
    setSelectedHymnId(hymn.id);
    setMessage("");
    setError("");
  }

  async function handleSaveTitle(hymn) {
    setBusyId(`title-${hymn.id}`);
    setMessage("");
    setError("");

    try {
      const updated = await updateChurchHymnTitle(hymn.id, draftTitle);
      setHymns((current) =>
        current.map((entry) => (entry.id === updated.id ? updated : entry))
      );
      setSelectedHymnId(updated.id);
      setEditingId("");
      setDraftTitle("");
      setMessage(`Saved title for #${updated.songNumber || "?"}.`);
    } catch (actionError) {
      console.error("Hymn title save error:", actionError);
      setError(actionError?.message || "Could not save the hymn title.");
    } finally {
      setBusyId("");
    }
  }

  function movePreviewSlide(direction) {
    if (!selectedSlides.length) return;

    setPreviewSlideIndex((current) => {
      const nextIndex = Math.min(
        Math.max(current + direction, 0),
        selectedSlides.length - 1
      );

      if (nextIndex !== current) {
        setPreviewSlideLoading(Boolean(selectedSlides[nextIndex]?.imageUrl));
      }

      return nextIndex;
    });
  }

  function closeHymnPreviewModal() {
    setSelectedHymnId("");
    setEditingId("");
    setDraftTitle("");
  }

  const showOverview = !useStandaloneRoutes || activeSection === "overview";
  const showSelector = !useStandaloneRoutes || activeSection === "selector";
  const showLibrary = !useStandaloneRoutes || activeSection === "library";
  const showSettings = useStandaloneRoutes && activeSection === "settings";
  const workspaceName = getWorkspaceName(user, organizationAccount);
  const organizationLogoUrl = getOrganizationLogoUrl(user, organizationAccount);

  function renderLibraryHymnRow(hymn) {
    const isFavorite = favoriteHymnIdSet.has(hymn.id);

    return (
      <div
        key={hymn.id}
        style={{
          ...styles.hymnRow,
          ...(selectedHymn?.id === hymn.id ? styles.hymnRowActive : {}),
        }}
      >
        <button
          type="button"
          style={styles.hymnRowSelectButton}
          onClick={() => setSelectedHymnId(hymn.id)}
        >
          <span style={styles.hymnNumber}>#{hymn.songNumber || "?"}</span>
          <span style={styles.hymnRowMain}>
            <strong>{getHymnLabel(hymn)}</strong>
            <small>{hymn.sourceFileName || (hymn.isGlobal ? "Global hymn" : "Church library")}</small>
          </span>
          <span style={styles.badge}>
            {usageCounts[hymn.id] || 0} chosen
          </span>
        </button>
        <button
          type="button"
          style={{
            ...styles.favoriteIconButton,
            ...(isFavorite ? styles.favoriteIconButtonActive : {}),
          }}
          onClick={(event) => {
            event.stopPropagation();
            toggleFavoriteHymn(hymn);
          }}
          title={isFavorite ? "Remove favorite" : "Add favorite"}
          aria-label={isFavorite ? "Remove favorite" : "Add favorite"}
        >
          <Star size={18} fill={isFavorite ? "currentColor" : "none"} />
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <PanelLoadingState
        iconAlt="Hymns"
        iconSrc={HymnsIcon}
        title="Loading Hymns"
        detail="Reading your hymn library, song counts, and service selections..."
      />
    );
  }

  return (
    <div style={useStandaloneRoutes ? { ...styles.hymnsShell, ...(isPhone ? styles.hymnsShellPhone : {}) } : styles.embeddedShell}>
      {useStandaloneRoutes ? (
        <aside style={{ ...styles.sideNav, ...(isPhone ? styles.sideNavPhone : {}) }} aria-label="Hymns sections">
          <div style={{ ...styles.sideNavBrand, ...(isPhone ? styles.sideNavBrandPhone : {}) }}>
            <img src={ChurchLogo} alt="Oikos Church" style={styles.sideNavBrandLogo} />
            <span style={styles.sideNavBrandLine} />
          </div>
          <div style={{ ...styles.sideNavOrg, ...(isPhone ? styles.sideNavOrgPhone : {}) }}>
            <img src={organizationLogoUrl} alt={workspaceName} style={styles.sideNavLogo} />
            <div style={styles.sideNavOrgCopy}>
              <span style={styles.sideNavOrgLabel}>Organization</span>
              <strong style={styles.sideNavOrgName}>{workspaceName}</strong>
            </div>
          </div>
          <div style={{ ...styles.sideNavDivider, ...(isPhone ? styles.sideNavDividerPhone : {}) }} />
          <div style={{ ...styles.navMain, ...(isPhone ? styles.navMainPhone : {}) }}>
            {HYMNS_SECTIONS.map((section) => {
              const Icon = section.icon;
              const active = activeSection === section.id;

              return (
                <button
                  key={section.id}
                  type="button"
                  style={{
                    ...styles.navButton,
                    ...(isPhone ? styles.navButtonPhone : {}),
                    ...(active ? styles.navButtonActive : {}),
                    ...(active ? { background: "var(--color-primary)" } : {}),
                    ...(isPhone && active ? styles.navButtonActivePhone : {}),
                  }}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={(event) => {
                    event.currentTarget.blur();
                    setActiveSection(section.id);
                  }}
                >
                  <Icon size={isPhone ? 15 : 17} />
                  <span style={isPhone ? styles.navLabelPhone : null}>
                    {isPhone ? section.shortLabel || section.label : section.label}
                  </span>
                </button>
              );
            })}
          </div>
          <div style={{ ...styles.sideNavFooter, ...(isPhone ? styles.sideNavFooterPhone : {}) }}>
            <button
              type="button"
              style={{ ...styles.footerButton, ...(isPhone ? styles.footerButtonPhone : {}) }}
              onMouseDown={(event) => event.preventDefault()}
              onClick={(event) => {
                event.currentTarget.blur();
                navigate("/church");
              }}
            >
              <ArrowLeft size={isPhone ? 15 : 16} />
              <span style={isPhone ? styles.navLabelPhone : null}>Apps</span>
            </button>
            {!isPhone ? (
              <button
                type="button"
                style={styles.footerButton}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => navigate("/church/management")}
              >
                <Building2 size={16} />
                <span>Management</span>
              </button>
            ) : null}
          </div>
        </aside>
      ) : null}

      <main style={{
        ...styles.content,
        ...(useStandaloneRoutes ? styles.contentStandalone : {}),
        ...(isPhone ? styles.contentPhone : {}),
        }}>
        <header style={{ ...styles.pageHeader, ...(isPhone ? styles.pageHeaderPhone : {}) }}>
          <span aria-hidden="true" style={styles.pageHeaderShapeOne} />
          <span aria-hidden="true" style={styles.pageHeaderShapeTwo} />
          <div style={{ ...styles.pageHeaderMain, ...(isPhone ? styles.pageHeaderMainPhone : {}) }}>
            <img src={HymnsIcon} alt="" style={styles.pageHeaderIcon} />
            <div style={styles.pageHeaderCopy}>
              <div style={styles.pageHeaderEyebrow}>Oikos Church Hymns</div>
              <h1 style={styles.pageTitle}>{workspaceName}</h1>
              <div style={styles.pageMeta}>Searchable hymn system, service selections, and personal favorites.</div>
            </div>
          </div>
          <div style={{ ...styles.pageHeaderActions, ...(isPhone ? styles.pageHeaderActionsPhone : {}) }}>
            <div style={styles.pageHeaderSectionLabel}>Section</div>
            <div style={styles.pageHeaderSectionName}>
              {HYMNS_SECTIONS.find((section) => section.id === activeSection)?.label || "Hymns"}
            </div>
            <button type="button" style={styles.refreshButton} onClick={refreshHymns}>
              <RefreshCcw size={16} />
              Refresh
            </button>
          </div>
        </header>

        {message ? <div style={styles.message}>{message}</div> : null}
        {error ? <div style={styles.error}>{error}</div> : null}

        {favoritePlanHymn ? (
          <div style={styles.modalBackdrop} role="presentation" onMouseDown={closeFavoritePlanModal}>
            <div
              style={styles.favoritePlanModal}
              role="dialog"
              aria-modal="true"
              aria-label="Add favorite hymn to service list"
              onMouseDown={(event) => event.stopPropagation()}
            >
              <div style={styles.modalHeader}>
                <div>
                  <div style={styles.modalEyebrow}>Add Favorite</div>
                  <div style={styles.modalTitle}>{getHymnLabel(favoritePlanHymn)}</div>
                  <div style={styles.selectorMeta}>#{favoritePlanHymn.songNumber || "?"}</div>
                </div>
                <button
                  type="button"
                  style={styles.modalCloseButton}
                  onClick={closeFavoritePlanModal}
                  title="Close"
                >
                  <X size={17} />
                </button>
              </div>

              <div style={styles.modalFormGrid}>
                <label style={styles.modalField}>
                  Week Of
                  <input
                    type="date"
                    value={favoritePlanWeekStart}
                    onChange={(event) => setFavoritePlanWeekStart(event.target.value || getCurrentHymnWeekStartDate())}
                    style={styles.weekInput}
                  />
                </label>
                <label style={styles.modalField}>
                  Location
                  <select
                    value={favoritePlanSlotIndex}
                    onChange={(event) => setFavoritePlanSlotIndex(Number(event.target.value) || 1)}
                    style={styles.weekSlotSelect}
                  >
                    {selectedWeekSlots.map((slot) => (
                      <option key={slot.slotIndex} value={slot.slotIndex}>
                        {slot.slotIndex}. {slot.label || `Hymn ${slot.slotIndex}`}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div style={styles.modalActions}>
                <button
                  type="button"
                  style={styles.secondaryButton}
                  onClick={closeFavoritePlanModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  style={styles.primaryButton}
                  onClick={handleAddFavoriteToServiceList}
                  disabled={busyId === "favorite-plan"}
                >
                  {busyId === "favorite-plan" ? "Adding..." : "Add to Service List"}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {selectedHymn ? (
          <div style={styles.modalBackdrop} role="presentation" onMouseDown={closeHymnPreviewModal}>
            <div
              style={styles.hymnPreviewModal}
              role="dialog"
              aria-modal="true"
              aria-label={`${getHymnLabel(selectedHymn)} preview`}
              onMouseDown={(event) => event.stopPropagation()}
            >
              <div style={styles.modalHeader}>
                <div>
                  <div style={styles.modalEyebrow}>Hymn Preview</div>
                  <div style={styles.modalTitle}>{getHymnLabel(selectedHymn)}</div>
                  <div style={styles.selectorMeta}>#{selectedHymn.songNumber || "?"}</div>
                </div>
                <button
                  type="button"
                  style={styles.modalCloseButton}
                  onClick={closeHymnPreviewModal}
                  title="Close"
                >
                  <X size={17} />
                </button>
              </div>

              <div style={styles.detailHeader}>
                <div style={styles.fileMeta}>
                  {selectedHymn.sourceFileName || (selectedHymn.isGlobal ? "Global hymn library" : "Manual church record")}
                </div>
                <button
                  type="button"
                  style={{
                    ...styles.favoriteDetailButton,
                    ...(favoriteHymnIdSet.has(selectedHymn.id) ? styles.favoriteDetailButtonActive : {}),
                  }}
                  onClick={() => toggleFavoriteHymn(selectedHymn)}
                >
                  <Star size={16} fill={favoriteHymnIdSet.has(selectedHymn.id) ? "currentColor" : "none"} />
                  {favoriteHymnIdSet.has(selectedHymn.id) ? "Favorited" : "Favorite"}
                </button>
              </div>

              {editingId === selectedHymn.id ? (
                <div style={styles.editRow}>
                  <input
                    type="text"
                    value={draftTitle}
                    onChange={(event) => setDraftTitle(event.target.value)}
                    placeholder="Enter hymn name"
                    style={styles.editInput}
                  />
                  <button
                    type="button"
                    style={styles.smallActionButton}
                    onClick={() => handleSaveTitle(selectedHymn)}
                    disabled={busyId === `title-${selectedHymn.id}`}
                  >
                    {busyId === `title-${selectedHymn.id}` ? "Saving..." : "Save"}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  style={styles.smallGhostButton}
                  onClick={() => startEditingTitle(selectedHymn)}
                >
                  Edit Name
                </button>
              )}

              <div style={styles.previewFrame}>
                <div style={styles.previewToolbar}>
                  <button
                    type="button"
                    style={styles.previewNavButton}
                    onClick={() => movePreviewSlide(-1)}
                    disabled={previewSlideIndex <= 0}
                    title="Previous slide"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <span style={styles.previewCounter}>
                    {selectedSlides.length
                      ? `${previewSlideIndex + 1} / ${selectedSlides.length}`
                      : "No slides"}
                  </span>
                  <button
                    type="button"
                    style={styles.previewNavButton}
                    onClick={() => movePreviewSlide(1)}
                    disabled={previewSlideIndex >= selectedSlides.length - 1}
                    title="Next slide"
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
                <div style={styles.previewBody}>
                  {selectedPreviewSlide?.imageUrl ? (
                    <>
                      {previewSlideLoading ? (
                        <span style={styles.previewLoading}>Loading slide...</span>
                      ) : null}
                      <img
                        key={selectedPreviewSlide.imageUrl}
                        src={selectedPreviewSlide.imageUrl}
                        alt={`${getHymnLabel(selectedHymn)} slide ${previewSlideIndex + 1}`}
                        style={{
                          ...styles.previewImage,
                          ...(previewSlideLoading ? styles.previewImageLoading : {}),
                        }}
                        onLoad={() => setPreviewSlideLoading(false)}
                        onError={() => setPreviewSlideLoading(false)}
                      />
                    </>
                  ) : (
                    selectedPreviewSlide?.body ||
                    "This hymn is searchable and ready for the service queue. Slide previews appear here when files include slide images."
                  )}
                </div>
              </div>

              <div style={{ ...styles.actionRow, ...(isPhone ? styles.actionRowPhone : {}) }}>
                <button
                  type="button"
                  style={styles.addButton}
                  onClick={() => handleAddToService(selectedHymn)}
                  disabled={busyId === selectedHymn.id}
                >
                  {busyId === selectedHymn.id ? "Adding..." : "Add to Service"}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {showOverview ? (
          <section style={styles.section}>
            <div style={styles.statsGrid}>
              <div style={styles.statCard}>
                <div style={styles.statIcon}><Music size={20} /></div>
                <div>
                  <div style={styles.statValue}>{hymns.length}</div>
                  <div style={styles.statLabel}>Total hymns</div>
                </div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statIcon}><Hash size={20} /></div>
                <div>
                  <div style={styles.statValue}>
                    {hymns.filter((hymn) => hymn.songNumber).length}
                  </div>
                  <div style={styles.statLabel}>Numbered songs</div>
                </div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statIcon}><Star size={20} fill="currentColor" /></div>
                <div>
                  <div style={styles.statValue}>{favoriteHymns.length}</div>
                  <div style={styles.statLabel}>Favorites</div>
                </div>
              </div>
            </div>

            <div style={styles.overviewPanelGrid}>
              <div style={styles.panel}>
                <div style={styles.sectionTitle}>Favorite Hymns</div>
                <div style={styles.topSongList}>
                  {favoriteHymns.length ? favoriteHymns.slice(0, 6).map((hymn) => (
                    <button
                      key={hymn.id}
                      type="button"
                      style={styles.topSongRow}
                      onClick={() => openFavoritePlanModal(hymn)}
                    >
                      <span style={{ ...styles.rank, ...styles.favoriteRank }}>
                        <Star size={15} fill="currentColor" />
                      </span>
                      <span style={styles.topSongMain}>
                        <strong>{getHymnLabel(hymn)}</strong>
                        <span>#{hymn.songNumber || "?"}</span>
                      </span>
                      <span style={styles.chosenCount}>Favorite</span>
                    </button>
                  )) : (
                    <div style={styles.emptyText}>Star hymns in the library to build your favorites.</div>
                  )}
                </div>
              </div>

              <div style={styles.panel}>
                <div style={styles.sectionTitle}>Most Chosen Songs</div>
                <div style={styles.topSongList}>
                  {topHymns.length ? topHymns.map((hymn, index) => (
                    <button
                      key={hymn.id}
                      type="button"
                      style={styles.topSongRow}
                      onClick={() => {
                        setSelectedHymnId(hymn.id);
                        setQuery(hymn.songNumber || hymn.title || "");
                        setActiveSection("library");
                      }}
                    >
                      <span style={styles.rank}>{index + 1}</span>
                      <span style={styles.topSongMain}>
                        <strong>{getHymnLabel(hymn)}</strong>
                        <span>#{hymn.songNumber || "?"}</span>
                      </span>
                      <span style={styles.chosenCount}>
                        {hymn.usageCount || 0} chosen
                      </span>
                    </button>
                  )) : (
                    <div style={styles.emptyText}>No hymns have been added yet.</div>
                  )}
                </div>
              </div>
            </div>
          </section>
        ) : null}

        {showSelector ? (
          <section style={styles.section}>
            <div style={{ ...styles.selectorHeader, ...(isPhone ? styles.selectorHeaderPhone : {}) }}>
              <div>
                <div style={styles.sectionTitle}>Hymn Selector</div>
                <div style={styles.selectorMeta}>
                  Set the service song order for the Today's Hymns live-display slide.
                </div>
              </div>
            </div>

            <div style={{ ...styles.selectorLayout, ...(isPhone ? styles.selectorLayoutPhone : {}) }}>
              <div style={styles.pastWeeksPanel}>
                <div style={styles.pastWeeksHeader}>
                  <div>
                    <div style={styles.pastWeeksTitle}>Saved Weeks</div>
                    <div style={styles.selectorMeta}>Open a past week to review or reuse its hymn list.</div>
                  </div>
                  <button
                    type="button"
                    style={styles.smallRefreshButton}
                    onClick={refreshSavedWeekPlans}
                    disabled={selectorLoading}
                    title="Refresh saved weeks"
                  >
                    <RefreshCcw size={15} />
                  </button>
                </div>
                <div style={styles.pastWeeksList}>
                  {savedWeekPlans.length ? savedWeekPlans.map((plan) => (
                    <button
                      key={plan.id || plan.weekStart}
                      type="button"
                      style={{
                        ...styles.pastWeekButton,
                        ...(plan.weekStart === selectorWeekStart ? styles.pastWeekButtonActive : {}),
                      }}
                      onClick={() => setSelectorWeekStart(plan.weekStart)}
                    >
                      <span style={styles.pastWeekDate}>{plan.weekStart}</span>
                      <span style={styles.pastWeekCount}>
                        {plan.hymnCount} hymn{plan.hymnCount === 1 ? "" : "s"}
                      </span>
                    </button>
                  )) : (
                    <div style={styles.emptyText}>Saved weeks will appear here after a weekly order is created.</div>
                  )}
                </div>
              </div>

              <div style={styles.weekPlanPanel}>
                <div style={styles.weekPlanHeader}>
                  <div>
                    <div style={styles.weekPlanTitle}>Service Song Order</div>
                    <div style={styles.selectorMeta}>
                      {selectedWeekHymns.length} selected for {selectorWeekStart}. Search inside a block to place a hymn there.{" "}
                      {weeklyPlanSaveStatus === "Saving..."
                        ? "Saving live..."
                        : weeklyPlanSaveStatus === "Save failed"
                          ? "Live save needs attention."
                          : "Live save on."}
                    </div>
                  </div>
                  <label style={styles.weekField}>
                    Week Of
                    <input
                      type="date"
                      value={selectorWeekStart}
                      onChange={(event) => setSelectorWeekStart(event.target.value || getCurrentHymnWeekStartDate())}
                      style={styles.weekInput}
                    />
                  </label>
                </div>

                <div style={styles.weekPlanList}>
                  {selectedWeekSlots.map((slot) => {
                    const hymn = slot.hymn;
                    const slotQuery = weekSlotQueries[slot.slotIndex] || "";
                    const hasSlotSearch = slotQuery.trim().length > 0;
                    const showSlotFavorites = Boolean(weekSlotFavoriteLists[slot.slotIndex]);
                    const slotResults = hasSlotSearch
                      ? searchChurchHymns(hymns, slotQuery)
                        .slice(0, 5)
                        .filter((optionHymn) => optionHymn?.id !== slot.hymnId)
                      : [];
                    const slotFavoriteResults = favoriteHymns.filter(
                      (optionHymn) => optionHymn?.id !== slot.hymnId
                    );

                    return (
                      <div
                        key={slot.slotIndex}
                        style={styles.weekSlotCard}
                        onClick={() => setActiveWeekSlotIndex(slot.slotIndex)}
                      >
                        <span style={styles.weekSlotCardTop}>
                          <span style={styles.weekSlotNumberWrap}>
                            <span style={styles.weekSlotNumberText}>
                              {hymn?.songNumber || slot.slotIndex}
                            </span>
                          </span>
                          <small style={styles.weekSlotBadge}>{slot.label}</small>
                        </span>
                        <strong style={styles.weekSlotCardTitle}>
                          {hymn ? getHymnLabel(hymn) : "No hymn selected"}
                        </strong>
                        <span style={styles.weekSlotDetailPanel}>
                          <span style={styles.weekSlotDetailRow}>
                            <span style={styles.weekSlotDetailLabel}>Block</span>
                            <span style={styles.weekSlotDetailValue}>{slot.label}</span>
                          </span>
                          <span style={{ ...styles.weekSlotDetailRow, ...styles.weekSlotDetailRowLast }}>
                            <span style={styles.weekSlotDetailLabel}>Song</span>
                            <span style={styles.weekSlotDetailValue}>
                              {hymn?.songNumber ? `#${hymn.songNumber}` : "Not selected"}
                            </span>
                          </span>
                        </span>
                        <div style={styles.weekSlotSearchWrap}>
                          <div style={styles.weekSlotSearchBox}>
                            <Search size={15} color="#64748b" />
                            <input
                              type="text"
                              value={slotQuery}
                              onChange={(event) => updateWeekSlotSearch(slot.slotIndex, event.target.value)}
                              onFocus={() => setActiveWeekSlotIndex(slot.slotIndex)}
                              placeholder={`Search ${slot.label || `block ${slot.slotIndex}`}`}
                              style={styles.weekSlotSearchInput}
                            />
                            <button
                              type="button"
                              style={{
                                ...styles.weekSlotFavoriteButton,
                                ...(showSlotFavorites ? styles.weekSlotFavoriteButtonActive : {}),
                              }}
                              onClick={() => toggleWeekSlotFavorites(slot.slotIndex)}
                              title="Show favorites"
                              aria-label="Show favorites"
                            >
                              <Star size={14} fill={showSlotFavorites ? "currentColor" : "none"} />
                            </button>
                          </div>
                          {showSlotFavorites ? (
                            <div style={styles.weekSlotFavoritesDropdown}>
                              {slotFavoriteResults.length ? slotFavoriteResults.map((optionHymn) => (
                                <button
                                  key={optionHymn.id}
                                  type="button"
                                  style={styles.weekSlotResultButton}
                                  onClick={() => addHymnToWeekSlot(optionHymn, slot.slotIndex)}
                                >
                                  <span style={styles.weekSlotResultNumber}>#{optionHymn.songNumber || "?"}</span>
                                  <span style={styles.weekSlotResultTitle}>{getHymnLabel(optionHymn)}</span>
                                  <Plus size={14} />
                                </button>
                              )) : (
                                <div style={styles.weekSlotEmptySearch}>No favorites yet</div>
                              )}
                            </div>
                          ) : null}
                        </div>
                        {hasSlotSearch ? (
                          <div style={styles.weekSlotResults}>
                            {slotResults.length ? slotResults.map((optionHymn) => (
                              <button
                                key={optionHymn.id}
                                type="button"
                                style={styles.weekSlotResultButton}
                                onClick={() => addHymnToWeekSlot(optionHymn, slot.slotIndex)}
                              >
                                <span style={styles.weekSlotResultNumber}>#{optionHymn.songNumber || "?"}</span>
                                <span style={styles.weekSlotResultTitle}>{getHymnLabel(optionHymn)}</span>
                                <Plus size={14} />
                              </button>
                            )) : (
                              <div style={styles.weekSlotEmptySearch}>No matches</div>
                            )}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>
        ) : null}

        {showLibrary ? (
          <section style={styles.section}>
            <div style={styles.searchCard}>
              <Search size={18} color="#64748b" />
              <input
                type="text"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search by song number, name, or file"
                style={styles.searchInput}
              />
              <div style={styles.searchMeta}>
                {filteredHymns.length} hymn{filteredHymns.length === 1 ? "" : "s"}
              </div>
            </div>

            {filteredHymns.length === 0 ? (
              <div style={styles.emptyCard}>
                <div style={styles.emptyTitle}>No hymns found</div>
                <div style={styles.emptyText}>
                  Search a different song number or hymn name.
                </div>
              </div>
            ) : (
              <div style={{ ...styles.libraryLayout, ...(isPhone ? styles.libraryLayoutPhone : {}) }}>
                <div style={styles.libraryList}>
                  {hasLibrarySearch ? (
                    filteredHymns.map((hymn) => renderLibraryHymnRow(hymn))
                  ) : (
                    libraryHymnGroups.map((group) => {
                      const isExpanded = expandedLibraryGroups[group.id] === true;

                      return (
                        <div key={group.id} style={styles.libraryGroup}>
                          <button
                            type="button"
                            style={styles.libraryGroupButton}
                            onClick={() =>
                              setExpandedLibraryGroups((current) => ({
                                ...current,
                                [group.id]: !isExpanded,
                              }))
                            }
                          >
                            <span style={styles.libraryGroupMain}>
                              <strong>{group.label}</strong>
                              <small>
                                {group.hymns.length} hymn{group.hymns.length === 1 ? "" : "s"}
                              </small>
                            </span>
                            <ChevronRight
                              size={18}
                              style={{
                                ...styles.libraryGroupChevron,
                                ...(isExpanded ? styles.libraryGroupChevronOpen : {}),
                              }}
                            />
                          </button>

                          {isExpanded ? (
                            <div style={styles.libraryGroupList}>
                              {group.hymns.map((hymn) => renderLibraryHymnRow(hymn))}
                            </div>
                          ) : null}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </section>
        ) : null}

        {showSettings ? (
          <section style={styles.section}>
            <HymnsSettings embedded onSlotLabelsSaved={applyWeeklySlotLabels} />
          </section>
        ) : null}

      </main>
    </div>
  );
}

const styles = {
  hymnsShell: {
    background: "#f6f8f5",
    boxSizing: "border-box",
    color: "#0f172a",
    display: "grid",
    gridTemplateColumns: "280px minmax(0, 1fr)",
    height: "100dvh",
    minHeight: "100dvh",
    overflow: "hidden",
  },
  hymnsShellPhone: {
    display: "block",
    height: "auto",
    overflow: "visible",
  },
  embeddedShell: {
    display: "block",
  },
  sideNav: {
    background: "rgba(255,255,255,0.96)",
    borderRight: "1px solid #d8e5d0",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    gap: 12,
    height: "100dvh",
    minHeight: "100dvh",
    overflow: "hidden",
    padding: 16,
  },
  sideNavPhone: {
    ...churchMobileNavStyles.dock,
    gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
  },
  sideNavBrand: {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    gap: 10,
    justifyContent: "center",
    padding: "8px 0 4px",
  },
  sideNavBrandPhone: {
    display: "none",
  },
  sideNavBrandLogo: {
    display: "block",
    height: "auto",
    maxHeight: 92,
    maxWidth: 196,
    objectFit: "contain",
    width: "90%",
  },
  sideNavBrandLine: {
    background: "linear-gradient(90deg, transparent, rgba(var(--color-primary-rgb),0.28), transparent)",
    height: 1,
    width: "100%",
  },
  sideNavOrg: {
    alignItems: "center",
    background: "rgba(255,255,255,0.78)",
    border: "1px solid rgba(var(--color-primary-rgb),0.18)",
    borderRadius: 18,
    boxShadow: "0 10px 22px rgba(15,23,42,0.08)",
    display: "flex",
    flexDirection: "column",
    gap: 12,
    margin: "0 0 6px",
    minWidth: 0,
    padding: "18px 14px 16px",
    textAlign: "center",
  },
  sideNavOrgPhone: {
    display: "none",
  },
  sideNavLogo: {
    display: "block",
    height: "auto",
    maxHeight: 146,
    maxWidth: 212,
    objectFit: "contain",
    width: "100%",
  },
  sideNavOrgCopy: {
    minWidth: 0,
    width: "100%",
  },
  sideNavOrgLabel: {
    color: "#64748b",
    fontSize: 11,
    fontWeight: 900,
    textTransform: "uppercase",
  },
  sideNavOrgName: {
    color: "#0f172a",
    display: "block",
    fontSize: 15,
    fontWeight: 800,
    lineHeight: 1.18,
    overflowWrap: "anywhere",
  },
  sideNavDivider: {
    background: "linear-gradient(90deg, transparent, rgba(var(--color-primary-rgb),0.24), transparent)",
    height: 1,
    margin: "2px 8px 4px",
  },
  sideNavDividerPhone: {
    display: "none",
  },
  navMain: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    gap: 8,
  },
  navMainPhone: {
    display: "contents",
  },
  navButton: {
    alignItems: "center",
    background: "rgba(var(--color-primary-rgb),0.09)",
    border: "1px solid transparent",
    borderRadius: 999,
    color: "var(--color-primary-dark)",
    cursor: "pointer",
    display: "flex",
    font: "inherit",
    fontSize: 13,
    fontWeight: 850,
    gap: 9,
    minHeight: 42,
    outline: "none",
    padding: "0 13px",
    textAlign: "left",
    width: "100%",
  },
  navButtonPhone: {
    ...churchMobileNavStyles.item,
  },
  navButtonActive: {
    boxShadow: "none",
    color: "#ffffff",
  },
  navButtonActivePhone: {
    ...churchMobileNavStyles.activeItem,
  },
  navLabelPhone: {
    ...churchMobileNavStyles.label,
  },
  sideNavFooter: {
    borderTop: "1px solid rgba(var(--color-primary-rgb),0.14)",
    display: "flex",
    flexDirection: "column",
    gap: 8,
    marginTop: "auto",
    paddingTop: 10,
  },
  sideNavFooterPhone: {
    display: "contents",
  },
  footerButton: {
    alignItems: "center",
    background: "rgba(var(--color-primary-rgb),0.12)",
    border: "1px solid transparent",
    borderRadius: 999,
    color: "var(--color-primary-dark)",
    cursor: "pointer",
    display: "flex",
    font: "inherit",
    fontSize: 13,
    fontWeight: 850,
    gap: 9,
    minHeight: 42,
    padding: "0 13px",
    textAlign: "left",
    width: "100%",
  },
  footerButtonPhone: {
    ...churchMobileNavStyles.item,
  },
  content: {
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    gap: 16,
    minHeight: 0,
    minWidth: 0,
    padding: 14,
  },
  contentStandalone: {
    height: "100dvh",
    overflowX: "hidden",
    overflowY: "auto",
    padding: "18px clamp(16px, 3vw, 32px) 32px",
  },
  contentPhone: {
    height: "auto",
    overflow: "visible",
    paddingBottom: 92,
  },
  pageHeader: {
    alignItems: "center",
    background:
      "radial-gradient(circle at 16% 18%, rgba(var(--color-primary-rgb),0.13), transparent 28%), radial-gradient(circle at 86% 0%, rgba(216,229,208,0.88), transparent 30%), linear-gradient(135deg, rgba(255,255,255,0.96), rgba(248,250,252,0.91))",
    backdropFilter: "blur(16px) saturate(1.08)",
    WebkitBackdropFilter: "blur(16px) saturate(1.08)",
    border: "1px solid #d8e5d0",
    borderRadius: 18,
    boxShadow: "0 10px 24px rgba(15,23,42,0.08)",
    display: "flex",
    gap: 16,
    justifyContent: "space-between",
    minHeight: 108,
    overflow: "hidden",
    padding: "16px 18px",
    position: "sticky",
    top: 0,
    zIndex: 70,
  },
  pageHeaderPhone: {
    alignItems: "flex-start",
    flexDirection: "column",
    gap: 12,
    top: 4,
  },
  pageHeaderShapeOne: {
    background: "rgba(var(--color-primary-rgb),0.08)",
    border: "1px solid rgba(var(--color-primary-rgb),0.08)",
    borderRadius: 999,
    height: 132,
    left: "38%",
    position: "absolute",
    top: -72,
    transform: "rotate(-18deg)",
    width: 260,
    zIndex: 0,
  },
  pageHeaderShapeTwo: {
    background: "rgba(255,255,255,0.58)",
    border: "1px solid rgba(95,125,77,0.10)",
    borderRadius: 999,
    bottom: -64,
    height: 110,
    position: "absolute",
    right: 28,
    transform: "rotate(14deg)",
    width: 210,
    zIndex: 0,
  },
  pageHeaderMain: {
    alignItems: "center",
    display: "flex",
    gap: 16,
    minWidth: 0,
    position: "relative",
    zIndex: 1,
  },
  pageHeaderMainPhone: {
    alignItems: "flex-start",
  },
  pageHeaderIcon: {
    display: "block",
    flex: "0 0 auto",
    height: 74,
    objectFit: "contain",
    width: 74,
  },
  pageHeaderCopy: {
    minWidth: 0,
  },
  pageHeaderEyebrow: {
    color: "var(--color-primary-dark)",
    fontSize: 13,
    fontWeight: 950,
    letterSpacing: 0,
    textTransform: "uppercase",
  },
  pageHeaderActions: {
    alignItems: "flex-end",
    display: "flex",
    flex: "0 0 auto",
    flexDirection: "column",
    gap: 7,
    maxWidth: 300,
    position: "relative",
    textAlign: "right",
    zIndex: 1,
  },
  pageHeaderActionsPhone: {
    alignItems: "flex-start",
    maxWidth: "100%",
    textAlign: "left",
    width: "100%",
  },
  pageHeaderSectionLabel: {
    color: "var(--color-primary-dark)",
    fontSize: 11,
    fontWeight: 950,
    textTransform: "uppercase",
  },
  pageHeaderSectionName: {
    color: "#0f172a",
    fontSize: 22,
    fontWeight: 950,
    lineHeight: 1.1,
    overflowWrap: "anywhere",
  },
  pageTitle: {
    color: "#0f172a",
    fontSize: 28,
    fontWeight: 950,
    lineHeight: 1.08,
    marginTop: 5,
    overflowWrap: "anywhere",
  },
  pageMeta: {
    color: "#64748b",
    fontSize: 12,
    fontWeight: 850,
    lineHeight: 1.3,
    marginTop: 6,
    overflowWrap: "anywhere",
  },
  refreshButton: {
    alignItems: "center",
    background: "rgba(255,255,255,0.7)",
    border: "1px solid rgba(95,125,77,0.14)",
    borderRadius: 999,
    color: "var(--color-primary-dark)",
    cursor: "pointer",
    display: "inline-flex",
    fontWeight: 950,
    gap: 8,
    justifyContent: "center",
    padding: "9px 12px",
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  statsGrid: {
    display: "grid",
    gap: 14,
    gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
  },
  statCard: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #dbe4ea",
    borderRadius: 18,
    display: "flex",
    gap: 14,
    padding: 18,
  },
  statIcon: {
    alignItems: "center",
    background: "rgba(var(--color-primary-rgb),0.12)",
    borderRadius: 14,
    color: "var(--color-primary-dark)",
    display: "flex",
    height: 44,
    justifyContent: "center",
    width: 44,
  },
  statValue: {
    color: "#0f172a",
    fontSize: 28,
    fontWeight: 900,
    lineHeight: 1,
  },
  statLabel: {
    color: "#64748b",
    fontSize: 13,
    fontWeight: 800,
    marginTop: 5,
  },
  panel: {
    background: "#ffffff",
    border: "1px solid #dbe4ea",
    borderRadius: 18,
    padding: 18,
  },
  overviewPanelGrid: {
    display: "grid",
    gap: 16,
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
  },
  sectionTitle: {
    color: "#0f172a",
    fontSize: "clamp(18px, 1.5vw, 22px)",
    fontWeight: 850,
  },
  topSongList: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    marginTop: 14,
  },
  topSongRow: {
    alignItems: "center",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 14,
    cursor: "pointer",
    display: "flex",
    gap: 12,
    padding: 12,
    textAlign: "left",
  },
  rank: {
    alignItems: "center",
    background: "var(--color-primary)",
    borderRadius: 999,
    color: "#ffffff",
    display: "flex",
    flex: "0 0 auto",
    fontSize: 13,
    fontWeight: 900,
    height: 30,
    justifyContent: "center",
    width: 30,
  },
  favoriteRank: {
    background: "#f59e0b",
  },
  topSongMain: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    gap: 3,
    minWidth: 0,
  },
  chosenCount: {
    color: "#64748b",
    fontSize: 12,
    fontWeight: 900,
  },
  searchCard: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #dbe4ea",
    borderRadius: 18,
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    padding: 14,
  },
  searchInput: {
    border: "none",
    color: "#0f172a",
    flex: 1,
    fontSize: 14,
    minWidth: 220,
    outline: "none",
  },
  searchMeta: {
    color: "#64748b",
    fontSize: 13,
    fontWeight: 800,
  },
  selectorHeader: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #dbe4ea",
    borderRadius: 18,
    display: "flex",
    gap: 16,
    justifyContent: "space-between",
    padding: 18,
  },
  selectorHeaderPhone: {
    alignItems: "stretch",
    flexDirection: "column",
  },
  selectorMeta: {
    color: "#64748b",
    fontSize: 13,
    fontWeight: 750,
    lineHeight: 1.5,
    marginTop: 4,
  },
  weekField: {
    color: "#334155",
    display: "flex",
    flex: "0 0 180px",
    flexDirection: "column",
    fontSize: 12,
    fontWeight: 900,
    gap: 6,
    textTransform: "uppercase",
  },
  weekInput: {
    border: "1px solid #d6dee6",
    borderRadius: 12,
    color: "#0f172a",
    fontSize: 14,
    fontWeight: 800,
    outline: "none",
    padding: "11px 12px",
  },
  selectorLayout: {
    alignItems: "start",
    display: "grid",
    gap: 20,
    gridTemplateColumns: "1fr",
    isolation: "isolate",
  },
  selectorLayoutPhone: {
    gridTemplateColumns: "1fr",
  },
  selectorPanel: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    minWidth: 0,
    order: 2,
  },
  pastWeeksPanel: {
    background: "#ffffff",
    border: "1px solid #dbe4ea",
    borderRadius: 18,
    display: "flex",
    flexDirection: "column",
    gap: 12,
    order: 0,
    padding: 14,
  },
  pastWeeksHeader: {
    alignItems: "flex-start",
    display: "flex",
    gap: 12,
    justifyContent: "space-between",
  },
  pastWeeksTitle: {
    color: "#0f172a",
    fontSize: 16,
    fontWeight: 850,
  },
  smallRefreshButton: {
    alignItems: "center",
    background: "#eef8f5",
    border: "1px solid #c1dcd4",
    borderRadius: 10,
    color: "#245a4d",
    cursor: "pointer",
    display: "inline-flex",
    height: 34,
    justifyContent: "center",
    padding: 0,
    width: 36,
  },
  pastWeeksList: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },
  pastWeekButton: {
    alignItems: "center",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    color: "#334155",
    cursor: "pointer",
    display: "inline-flex",
    flex: "0 1 auto",
    font: "inherit",
    gap: 8,
    padding: "9px 11px",
  },
  pastWeekButtonActive: {
    background: "rgba(var(--color-primary-rgb),0.12)",
    borderColor: "rgba(var(--color-primary-rgb),0.35)",
    color: "var(--color-primary-dark)",
  },
  pastWeekDate: {
    fontSize: 13,
    fontWeight: 900,
  },
  pastWeekCount: {
    color: "#64748b",
    fontSize: 12,
    fontWeight: 850,
  },
  selectorList: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  selectorListHeader: {
    alignItems: "center",
    display: "flex",
    gap: 10,
    justifyContent: "space-between",
    padding: "0 2px",
  },
  selectorListTitle: {
    color: "#0f172a",
    fontSize: 15,
    fontWeight: 900,
  },
  selectorFilterBar: {
    alignItems: "center",
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },
  selectorFilterButton: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #dbe4ea",
    borderRadius: 999,
    color: "#475569",
    cursor: "pointer",
    display: "inline-flex",
    font: "inherit",
    fontSize: 13,
    fontWeight: 900,
    gap: 7,
    minHeight: 36,
    padding: "8px 12px",
  },
  selectorFilterButtonActive: {
    background: "#fff7ed",
    borderColor: "#fdba74",
    color: "#d97706",
  },
  selectorFilterCount: {
    alignItems: "center",
    background: "rgba(15,23,42,0.08)",
    borderRadius: 999,
    display: "inline-flex",
    fontSize: 11,
    justifyContent: "center",
    minWidth: 24,
    padding: "3px 7px",
  },
  favoriteSelectorPanel: {
    background: "#fffaf0",
    border: "1px solid #fde3af",
    borderRadius: 18,
    display: "flex",
    flexDirection: "column",
    gap: 10,
    padding: 14,
  },
  favoriteSelectorHeader: {
    alignItems: "center",
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
  },
  favoriteCountPill: {
    background: "#f59e0b",
    borderRadius: 999,
    color: "#ffffff",
    fontSize: 12,
    fontWeight: 900,
    minWidth: 28,
    padding: "5px 9px",
    textAlign: "center",
  },
  favoriteSelectorList: {
    display: "grid",
    gap: 8,
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  },
  selectorFavoriteRow: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #fde3af",
    borderRadius: 14,
    color: "#92400e",
    display: "flex",
    gap: 10,
    minWidth: 0,
    padding: 8,
    textAlign: "left",
    width: "100%",
  },
  selectorHymnRow: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #dbe4ea",
    borderRadius: 14,
    display: "flex",
    gap: 12,
    padding: 10,
    textAlign: "left",
    width: "100%",
  },
  selectorRowSelectButton: {
    alignItems: "center",
    background: "transparent",
    border: "none",
    color: "inherit",
    cursor: "pointer",
    display: "flex",
    flex: 1,
    font: "inherit",
    gap: 12,
    minWidth: 0,
    padding: 4,
    textAlign: "left",
  },
  selectorHymnRowSelected: {
    background: "#f8fafc",
    color: "#64748b",
    cursor: "default",
  },
  selectorStarButton: {
    alignItems: "center",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 11,
    color: "#94a3b8",
    cursor: "pointer",
    display: "inline-flex",
    flex: "0 0 auto",
    height: 34,
    justifyContent: "center",
    padding: 0,
    width: 36,
  },
  selectorStarButtonActive: {
    background: "#fff7ed",
    borderColor: "#fdba74",
    color: "#d97706",
  },
  selectorAddIcon: {
    alignItems: "center",
    background: "#eef8f5",
    borderRadius: 999,
    color: "#245a4d",
    display: "inline-flex",
    flex: "0 0 auto",
    fontSize: 11,
    fontWeight: 900,
    gap: 3,
    justifyContent: "center",
    minHeight: 28,
    minWidth: 36,
    padding: "0 9px",
  },
  weekPlanPanel: {
    background: "#ffffff",
    border: "1px solid #dbe4ea",
    borderRadius: 18,
    display: "flex",
    flexDirection: "column",
    gap: 14,
    padding: 16,
    alignSelf: "start",
    maxWidth: "100%",
    minWidth: 0,
    order: 1,
    overflow: "hidden",
  },
  weekPlanHeader: {
    alignItems: "flex-start",
    display: "flex",
    gap: 12,
    justifyContent: "space-between",
  },
  weekPlanTitle: {
    color: "#0f172a",
    fontSize: 18,
    fontWeight: 850,
  },
  weekPlanList: {
    display: "grid",
    gap: 12,
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
  },
  weekPlanRow: {
    alignItems: "center",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 14,
    display: "flex",
    gap: 10,
    padding: 12,
  },
  weekSlotCard: {
    appearance: "none",
    WebkitAppearance: "none",
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.96))",
    border: "1px solid #d9e1ef",
    borderRadius: 18,
    boxShadow: "0 14px 34px rgba(23,32,51,0.1), inset 0 1px 0 rgba(255,255,255,0.72)",
    color: "#172033",
    cursor: "default",
    display: "grid",
    gap: 16,
    gridTemplateRows: "auto minmax(54px, auto) auto auto",
    minHeight: 280,
    outline: "none",
    padding: 20,
    position: "relative",
    textAlign: "left",
    transition: "box-shadow 160ms ease, transform 160ms ease",
  },
  weekSlotSelect: {
    background: "#ffffff",
    border: "1px solid #d6dee6",
    borderRadius: 10,
    color: "#0f172a",
    fontSize: 13,
    fontWeight: 750,
    minHeight: 38,
    outline: "none",
    padding: "8px 10px",
    width: "100%",
  },
  weekSlotCardTop: {
    alignItems: "center",
    display: "grid",
    gap: 10,
    gridTemplateColumns: "56px minmax(0, 1fr)",
  },
  weekSlotNumberWrap: {
    alignItems: "center",
    background: "linear-gradient(135deg, #dde6ff, #f5f7ff)",
    border: "1px solid #dbe5ff",
    borderRadius: 14,
    boxShadow: "0 10px 20px rgba(37,53,111,0.1), inset 0 1px 0 rgba(255,255,255,0.82)",
    display: "inline-flex",
    height: 56,
    justifyContent: "center",
    overflow: "hidden",
    width: 56,
  },
  weekSlotNumberText: {
    color: "#25356f",
    fontSize: 18,
    fontWeight: 950,
    maxWidth: "100%",
    overflow: "hidden",
    padding: "0 4px",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  weekSlotBadge: {
    background: "#eef3ff",
    border: "1px solid #dbe5ff",
    borderRadius: 999,
    color: "#25356f",
    display: "inline-flex",
    fontSize: 12,
    fontWeight: 800,
    justifySelf: "end",
    maxWidth: "100%",
    minWidth: 0,
    overflow: "hidden",
    padding: "4px 8px",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    width: "fit-content",
  },
  weekSlotCardTitle: {
    color: "#172033",
    fontSize: 21,
    fontWeight: 900,
    lineHeight: 1.16,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "normal",
  },
  weekSlotDetailPanel: {
    alignSelf: "stretch",
    background: "rgba(248,250,252,0.72)",
    border: "1px solid #e7edf6",
    borderRadius: 12,
    display: "grid",
    gap: 0,
    overflow: "hidden",
  },
  weekSlotDetailRow: {
    alignItems: "center",
    borderBottom: "1px solid #edf2f7",
    display: "grid",
    gap: 10,
    gridTemplateColumns: "72px minmax(0, 1fr)",
    minHeight: 22,
    padding: "8px 10px",
  },
  weekSlotDetailRowLast: {
    borderBottom: "none",
  },
  weekSlotDetailLabel: {
    color: "#8a93a5",
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: 0,
    textTransform: "uppercase",
  },
  weekSlotDetailValue: {
    color: "#667085",
    fontSize: 13,
    fontWeight: 800,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  weekSlotSearchWrap: {
    borderTop: "1px solid #e7edf6",
    paddingTop: 14,
    position: "relative",
  },
  weekSlotSearchBox: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #cfd9e8",
    borderRadius: 12,
    boxShadow: "0 8px 18px rgba(23,32,51,0.05)",
    display: "flex",
    gap: 8,
    minHeight: 40,
    padding: "0 10px",
  },
  weekSlotSearchInput: {
    background: "transparent",
    border: "none",
    color: "#0f172a",
    flex: 1,
    fontSize: 13,
    fontWeight: 800,
    minWidth: 0,
    outline: "none",
  },
  weekSlotFavoriteButton: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #d9e1ef",
    borderRadius: 8,
    color: "#94a3b8",
    cursor: "pointer",
    display: "inline-flex",
    flex: "0 0 auto",
    height: 30,
    justifyContent: "center",
    padding: 0,
    width: 32,
  },
  weekSlotFavoriteButtonActive: {
    background: "#fff7ed",
    borderColor: "#fdba74",
    color: "#d97706",
  },
  weekSlotFavoritesDropdown: {
    background: "#ffffff",
    border: "1px solid #d9e1ef",
    borderRadius: 8,
    boxShadow: "0 18px 42px rgba(23,32,51,0.14)",
    display: "grid",
    gap: 7,
    left: 0,
    maxHeight: 260,
    overflowY: "auto",
    padding: 8,
    position: "absolute",
    right: 0,
    top: "calc(100% + 8px)",
    zIndex: 20,
  },
  weekSlotResults: {
    display: "grid",
    gap: 7,
    maxHeight: 210,
    overflowY: "auto",
  },
  weekSlotResultButton: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #d9e1ef",
    borderRadius: 8,
    color: "#334155",
    cursor: "pointer",
    display: "grid",
    font: "inherit",
    gap: 8,
    gridTemplateColumns: "56px minmax(0, 1fr) 18px",
    minHeight: 38,
    padding: "8px 10px",
    textAlign: "left",
  },
  weekSlotResultNumber: {
    color: "var(--color-primary-dark)",
    fontSize: 12,
    fontWeight: 950,
  },
  weekSlotResultTitle: {
    color: "#0f172a",
    fontSize: 13,
    fontWeight: 850,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  weekSlotEmptySearch: {
    color: "#94a3b8",
    fontSize: 12,
    fontWeight: 800,
    padding: "8px 2px",
  },
  libraryLayout: {
    alignItems: "start",
    display: "grid",
    gap: 20,
    gridTemplateColumns: "1fr",
    isolation: "isolate",
  },
  libraryLayoutPhone: {
    gridTemplateColumns: "1fr",
  },
  libraryList: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    minWidth: 0,
  },
  libraryGroup: {
    background: "#ffffff",
    border: "1px solid #dbe4ea",
    borderRadius: 16,
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    maxWidth: "100%",
  },
  libraryGroupButton: {
    alignItems: "center",
    background: "#f8fafc",
    border: "none",
    boxSizing: "border-box",
    color: "#0f172a",
    cursor: "pointer",
    display: "flex",
    font: "inherit",
    justifyContent: "space-between",
    gap: 12,
    padding: "14px 16px",
    textAlign: "left",
    width: "100%",
  },
  libraryGroupMain: {
    display: "flex",
    flexDirection: "column",
    gap: 3,
    minWidth: 0,
  },
  libraryGroupChevron: {
    color: "#64748b",
    flex: "0 0 auto",
    transition: "transform 180ms ease",
  },
  libraryGroupChevronOpen: {
    transform: "rotate(90deg)",
  },
  libraryGroupList: {
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    gap: 8,
    padding: 10,
    width: "100%",
  },
  hymnRow: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #dbe4ea",
    borderRadius: 14,
    boxSizing: "border-box",
    display: "flex",
    gap: 12,
    maxWidth: "100%",
    padding: 10,
    textAlign: "left",
    width: "100%",
  },
  hymnRowSelectButton: {
    alignItems: "center",
    background: "transparent",
    border: "none",
    color: "inherit",
    cursor: "pointer",
    display: "flex",
    flex: 1,
    font: "inherit",
    gap: 12,
    minWidth: 0,
    padding: 4,
    textAlign: "left",
  },
  hymnRowActive: {
    borderColor: "#7ea087",
    boxShadow: "0 0 0 1px rgba(126, 160, 135, 0.2)",
  },
  favoriteIconButton: {
    alignItems: "center",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    color: "#94a3b8",
    cursor: "pointer",
    display: "inline-flex",
    flex: "0 0 auto",
    height: 38,
    justifyContent: "center",
    padding: 0,
    width: 40,
  },
  favoriteIconButtonActive: {
    background: "#fff7ed",
    borderColor: "#fdba74",
    color: "#d97706",
  },
  hymnNumber: {
    color: "var(--color-primary-dark)",
    flex: "0 0 64px",
    fontSize: 13,
    fontWeight: 900,
  },
  hymnRowMain: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    gap: 3,
    minWidth: 0,
  },
  badge: {
    background: "#eef3f7",
    borderRadius: 999,
    color: "#4b5f72",
    flex: "0 0 auto",
    fontSize: 11,
    fontWeight: 900,
    padding: "6px 10px",
  },
  detailCard: {
    background: "#ffffff",
    border: "1px solid #dbe4ea",
    borderRadius: 18,
    display: "flex",
    flexDirection: "column",
    gap: 12,
    padding: 16,
    alignSelf: "start",
    maxWidth: "100%",
    minWidth: 0,
    order: -1,
    overflow: "hidden",
    width: "100%",
  },
  detailHeader: {
    alignItems: "flex-start",
    display: "flex",
    gap: 12,
    justifyContent: "space-between",
  },
  detailHeaderActions: {
    alignItems: "flex-end",
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  favoriteDetailButton: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #d6dee6",
    borderRadius: 999,
    color: "#475569",
    cursor: "pointer",
    display: "inline-flex",
    font: "inherit",
    fontSize: 12,
    fontWeight: 900,
    gap: 7,
    padding: "8px 11px",
  },
  favoriteDetailButtonActive: {
    background: "#fff7ed",
    borderColor: "#fdba74",
    color: "#d97706",
  },
  songNumber: {
    color: "var(--color-primary-dark)",
    fontSize: 12,
    fontWeight: 900,
    textTransform: "uppercase",
  },
  songTitle: {
    color: "#0f172a",
    fontSize: 18,
    fontWeight: 850,
    marginTop: 4,
  },
  fileMeta: {
    color: "#64748b",
    fontSize: 12,
    lineHeight: 1.5,
  },
  editRow: {
    alignItems: "center",
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },
  editInput: {
    border: "1px solid #d6dee6",
    borderRadius: 10,
    color: "#0f172a",
    flex: 1,
    fontSize: 13,
    minWidth: 180,
    outline: "none",
    padding: "10px 12px",
  },
  previewFrame: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 14,
    overflow: "hidden",
  },
  previewToolbar: {
    alignItems: "center",
    borderBottom: "1px solid #e2e8f0",
    display: "flex",
    gap: 8,
    justifyContent: "space-between",
    minHeight: 42,
    padding: "6px 8px",
  },
  previewNavButton: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #d6dee6",
    borderRadius: 10,
    color: "#334155",
    cursor: "pointer",
    display: "inline-flex",
    height: 32,
    justifyContent: "center",
    padding: 0,
    width: 38,
  },
  previewCounter: {
    color: "#475569",
    fontSize: 12,
    fontWeight: 900,
  },
  previewBody: {
    alignItems: "center",
    color: "#334155",
    display: "flex",
    fontSize: 14,
    justifyContent: "center",
    lineHeight: 1.6,
    minHeight: 280,
    padding: 10,
    position: "relative",
    whiteSpace: "pre-wrap",
  },
  previewLoading: {
    background: "rgba(15, 23, 42, 0.72)",
    borderRadius: 999,
    color: "#ffffff",
    fontSize: 12,
    fontWeight: 850,
    left: "50%",
    padding: "7px 11px",
    position: "absolute",
    top: "50%",
    transform: "translate(-50%, -50%)",
    zIndex: 1,
  },
  previewImage: {
    borderRadius: 12,
    display: "block",
    maxHeight: 420,
    maxWidth: "100%",
    objectFit: "contain",
  },
  previewImageLoading: {
    opacity: 0.4,
  },
  liveControlPanel: {
    alignItems: "center",
    background: "#fff7ed",
    border: "1px solid #fed7aa",
    borderRadius: 14,
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    justifyContent: "space-between",
    padding: 10,
  },
  liveControlMeta: {
    color: "#7c2d12",
    display: "flex",
    flex: "1 1 160px",
    flexDirection: "column",
    fontSize: 12,
    gap: 3,
    minWidth: 0,
  },
  liveControlButtons: {
    alignItems: "center",
    display: "grid",
    gap: 8,
    gridTemplateColumns: "38px minmax(0, 1fr) 38px",
    minWidth: 0,
    width: "min(100%, 340px)",
  },
  liveControlButtonsPhone: {
    width: "100%",
  },
  liveControlButton: {
    alignItems: "center",
    background: "#b42318",
    border: "none",
    borderRadius: 10,
    color: "#ffffff",
    cursor: "pointer",
    display: "inline-flex",
    fontSize: 12,
    fontWeight: 850,
    justifyContent: "center",
    minHeight: 32,
    minWidth: 0,
    padding: "8px 10px",
    whiteSpace: "normal",
    width: "100%",
  },
  actionRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
  },
  actionRowPhone: {
    flexDirection: "column",
  },
  smallActionButton: {
    background: "var(--color-primary)",
    border: "none",
    borderRadius: 10,
    color: "#ffffff",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 800,
    padding: "10px 12px",
  },
  smallGhostButton: {
    background: "#ffffff",
    border: "1px solid #d6dee6",
    borderRadius: 10,
    color: "#334155",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 800,
    padding: "10px 12px",
  },
  addButton: {
    alignItems: "center",
    background: "var(--color-primary)",
    border: "none",
    borderRadius: 12,
    color: "#ffffff",
    cursor: "pointer",
    display: "inline-flex",
    fontSize: 14,
    fontWeight: 850,
    justifyContent: "center",
    flex: "1 1 150px",
    minWidth: 120,
    padding: "12px 14px",
    textAlign: "center",
    whiteSpace: "normal",
    width: "100%",
  },
  quickLiveButton: {
    alignItems: "center",
    background: "#b42318",
    border: "none",
    borderRadius: 12,
    color: "#ffffff",
    cursor: "pointer",
    display: "inline-flex",
    fontSize: 14,
    fontWeight: 850,
    justifyContent: "center",
    flex: "1 1 150px",
    minWidth: 120,
    padding: "12px 14px",
    textAlign: "center",
    whiteSpace: "normal",
    width: "100%",
  },
  uploadPanel: {
    background: "#ffffff",
    border: "1px solid #dbe4ea",
    borderRadius: 18,
    display: "flex",
    flexDirection: "column",
    gap: 16,
    maxWidth: 820,
    padding: 18,
  },
  formGrid: {
    display: "grid",
    gap: 14,
    gridTemplateColumns: "180px minmax(0, 1fr)",
  },
  formGridPhone: {
    gridTemplateColumns: "1fr",
  },
  fieldLabel: {
    color: "#334155",
    display: "flex",
    flexDirection: "column",
    fontSize: 13,
    fontWeight: 850,
    gap: 7,
  },
  fieldInput: {
    border: "1px solid #d6dee6",
    borderRadius: 12,
    color: "#0f172a",
    fontSize: 14,
    outline: "none",
    padding: "12px 14px",
  },
  fileDrop: {
    alignItems: "center",
    background: "#f8fafc",
    border: "1px dashed #b8c7d4",
    borderRadius: 14,
    color: "#334155",
    cursor: "pointer",
    display: "flex",
    gap: 10,
    fontWeight: 850,
    justifyContent: "center",
    minHeight: 92,
    padding: 16,
    textAlign: "center",
  },
  fileDropActive: {
    background: "#eef8f5",
    borderColor: "var(--color-primary)",
    boxShadow: "0 0 0 3px rgba(var(--color-primary-rgb),0.12)",
    color: "var(--color-primary-dark)",
  },
  hiddenFileInput: {
    display: "none",
  },
  uploadSlideList: {
    border: "1px solid #e2e8f0",
    borderRadius: 14,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  uploadSlideRow: {
    alignItems: "center",
    background: "#ffffff",
    borderBottom: "1px solid #e2e8f0",
    display: "grid",
    gap: 8,
    gridTemplateColumns: "34px minmax(0, 1fr) auto auto auto",
    minHeight: 46,
    padding: "8px 10px",
  },
  uploadSlideNumber: {
    alignItems: "center",
    background: "rgba(var(--color-primary-rgb),0.12)",
    borderRadius: 999,
    color: "var(--color-primary-dark)",
    display: "flex",
    fontSize: 12,
    fontWeight: 900,
    height: 28,
    justifyContent: "center",
    width: 28,
  },
  uploadSlideName: {
    color: "#334155",
    fontSize: 13,
    fontWeight: 800,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  uploadSlideButton: {
    background: "#f8fafc",
    border: "1px solid #d6dee6",
    borderRadius: 10,
    color: "#334155",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 800,
    padding: "7px 9px",
  },
  uploadButton: {
    alignSelf: "flex-start",
    background: "var(--color-primary)",
    border: "none",
    borderRadius: 12,
    color: "#ffffff",
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 850,
    padding: "13px 16px",
  },
  modalBackdrop: {
    alignItems: "center",
    background: "rgba(15, 23, 42, 0.48)",
    display: "flex",
    inset: 0,
    justifyContent: "center",
    padding: 18,
    position: "fixed",
    zIndex: 260,
  },
  favoritePlanModal: {
    background: "#ffffff",
    border: "1px solid #dbe4ea",
    borderRadius: 18,
    boxShadow: "0 24px 70px rgba(15, 23, 42, 0.24)",
    display: "flex",
    flexDirection: "column",
    gap: 16,
    maxWidth: 520,
    padding: 18,
    width: "min(100%, 520px)",
  },
  hymnPreviewModal: {
    background: "#ffffff",
    border: "1px solid #dbe4ea",
    borderRadius: 18,
    boxShadow: "0 24px 70px rgba(15, 23, 42, 0.24)",
    display: "flex",
    flexDirection: "column",
    gap: 14,
    maxHeight: "92vh",
    maxWidth: 760,
    overflowY: "auto",
    padding: 18,
    width: "min(100%, 760px)",
  },
  modalHeader: {
    alignItems: "flex-start",
    background:
      "radial-gradient(circle at 16% 18%, rgba(var(--color-primary-rgb),0.13), transparent 28%), radial-gradient(circle at 86% 0%, rgba(216,229,208,0.78), transparent 30%), linear-gradient(135deg, rgba(255,255,255,0.94), rgba(248,250,252,0.88))",
    backdropFilter: "blur(16px) saturate(1.08)",
    WebkitBackdropFilter: "blur(16px) saturate(1.08)",
    border: "1px solid #d8e5d0",
    borderRadius: 16,
    boxShadow: "0 10px 24px rgba(15,23,42,0.08)",
    display: "flex",
    gap: 14,
    justifyContent: "space-between",
    overflow: "hidden",
    padding: "14px 16px",
  },
  modalEyebrow: {
    color: "#d97706",
    fontSize: 12,
    fontWeight: 950,
    textTransform: "uppercase",
  },
  modalTitle: {
    color: "#0f172a",
    fontSize: 20,
    fontWeight: 900,
    marginTop: 4,
  },
  modalCloseButton: {
    alignItems: "center",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 10,
    color: "#334155",
    cursor: "pointer",
    display: "inline-flex",
    height: 36,
    justifyContent: "center",
    padding: 0,
    width: 38,
  },
  modalFormGrid: {
    display: "grid",
    gap: 12,
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  },
  modalField: {
    color: "#334155",
    display: "flex",
    flexDirection: "column",
    fontSize: 12,
    fontWeight: 900,
    gap: 7,
    textTransform: "uppercase",
  },
  modalActions: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    justifyContent: "flex-end",
  },
  secondaryButton: {
    background: "#ffffff",
    border: "1px solid #d6dee6",
    borderRadius: 12,
    color: "#334155",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 850,
    padding: "11px 14px",
  },
  primaryButton: {
    background: "var(--color-primary)",
    border: "none",
    borderRadius: 12,
    color: "#ffffff",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 900,
    padding: "11px 14px",
  },
  message: {
    background: "#edf7ec",
    border: "1px solid #cce1c8",
    borderRadius: 14,
    color: "#315a2f",
    fontSize: 13,
    fontWeight: 800,
    padding: "12px 14px",
  },
  error: {
    background: "#fff0f0",
    border: "1px solid #f2c9c9",
    borderRadius: 14,
    color: "#8f2f2f",
    fontSize: 13,
    fontWeight: 800,
    padding: "12px 14px",
  },
  emptyCard: {
    background: "#ffffff",
    border: "1px solid #dbe4ea",
    borderRadius: 18,
    padding: 20,
  },
  emptyTitle: {
    color: "#0f172a",
    fontSize: 18,
    fontWeight: 850,
  },
  emptyText: {
    color: "#64748b",
    fontSize: 14,
    lineHeight: 1.6,
    marginTop: 8,
  },
};
